import { useEffect, useState, useRef, useCallback } from "react";
import { Button, Card, Space, Table, Checkbox, Select, Modal, Tooltip, Tag, Spin } from "antd";
import useApi from "../../hooks/useApi";
import { getClass, getClassSlots, getEmployees } from "../../constants/API/API";
import { useNavigate, useParams } from "react-router";
import { Severty, ShowToast } from "../../helper/toast";
import { EyeOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import moment from "moment";

const PAGE_SIZE = 10;

const ClassViewPage = () => {
  const [slots, setSlots] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classData, setClassData] = useState({});
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [parents, setParents] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [parentLoading, setParentLoading] = useState(false);
  const [parentSearch, setParentSearch] = useState("");
  const [parentPage, setParentPage] = useState(1);
  const [parentTotal, setParentTotal] = useState(0);
  const parentSearchTimerRef = useRef(null);

  const [selectedParentId, setSelectedParentId] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentLoading, setStudentLoading] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const { request } = useRequest();
  const navigate = useNavigate();
  const { id } = useParams();

  const fetchParentsList = useCallback(
    ({ page = 1, search = "", append = false } = {}) => {
      setParentLoading(true);
      request({
        url: `${apiPath.parentsList}?roleType=parent&page=${page}&limit=${PAGE_SIZE}&search=${encodeURIComponent(search)}`,
        method: "GET",
        onSuccess: ({ data, status }) => {
          setParentLoading(false);
          if (status === 200) {
            const list = data?.list || [];
            const total = data?.total || 0;
            setParentTotal(total);
            setParents((prev) => (append ? [...prev, ...list] : list));
            setParentPage(page);
          }
        },
        onError: (err) => {
          setParentLoading(false);
          ShowToast(err?.message || "Failed to fetch parents", Severty.ERROR);
        },
      });
    },
    [request]
  );

  const handleParentSearch = (value) => {
    setParentSearch(value);
    if (parentSearchTimerRef.current) clearTimeout(parentSearchTimerRef.current);
    parentSearchTimerRef.current = setTimeout(() => {
      fetchParentsList({ page: 1, search: value, append: false });
    }, 400);
  };

  const handleParentPopupScroll = (e) => {
    const { target } = e;
    const isNearBottom =
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 20;
    const hasMore = parents.length < parentTotal;
    if (isNearBottom && hasMore && !parentLoading) {
      fetchParentsList({ page: parentPage + 1, search: parentSearch, append: true });
    }
  };

  const handleReservationClick = (record) => {
    setSelectedSlot(record);
    setParentSearch("");
    setParents([]);
    setStudents([]);
    setSelectedParentId(null);
    setSelectedStudentId(null);
    setSelectedStudent(null);
    setIsRecurring(false);
    setIsReservationModalOpen(true);
    fetchParentsList({ page: 1, search: "", append: false });
  };

  const handleCloseModal = () => {
    setIsReservationModalOpen(false);
    setParentSearch("");
    setParents([]);
    setStudents([]);
    setSelectedParentId(null);
    setSelectedStudentId(null);
    setSelectedStudent(null);
    setIsRecurring(false);
    setBookingLoading(false);
  };

  const fetchStudentsByParent = (parentId) => {
    setStudentLoading(true);
    request({
      url: `${apiPath.studentsList}/${parentId}`,
      method: "GET",
      onSuccess: ({ data, status }) => {
        setStudentLoading(false);
        if (status === 200) {
          setStudents(data?.list || []);
        }
      },
      onError: (err) => {
        setStudentLoading(false);
        ShowToast(err.message || "Failed to fetch students", Severty.ERROR);
      },
    });
  };

  const getUpcomingDateByDay = (day) => {
    const dayMap = {
      SUNDAY: 0,
      MONDAY: 1,
      TUESDAY: 2,
      WEDNESDAY: 3,
      THURSDAY: 4,
      FRIDAY: 5,
      SATURDAY: 6,
    };

    const today = moment();
    const targetDay = dayMap[day];

    let result = today.clone().day(targetDay);

    if (result.isSameOrBefore(today, "day")) {
      result.add(1, "week");
    }

    return result.startOf("day").format("YYYY-MM-DD 00:00:00.000");
  };

  const handleBookClass = () => {
    if (!selectedStudentId) {
      ShowToast("Please select student", Severty.ERROR);
      return;
    }
    const student = students.find((s) => s._id === selectedStudentId);
    const parent = parents.find((p) => p._id === selectedParentId);
    if (!student) {
      ShowToast("Selected student data not available yet", Severty.ERROR);
      return;
    }
    if (!parent) {
      ShowToast("Selected parent data not available yet", Severty.ERROR);
      return;
    }
    if (!parent.branchId) {
      ShowToast("Parent has not selected a branch yet", Severty.ERROR);
      return;
    }
    const bookingDate = getUpcomingDateByDay(selectedSlot.day);
    setBookingLoading(true);
    request({
      url: apiPath.bookClasses,
      method: "POST",
      data: {
        classId: id,
        slotId: selectedSlot?._id,
        date: bookingDate,
        slot: `${selectedSlot.startTime}-${selectedSlot.endTime}`,
        branchId: parent.branchId,
        trainerId: selectedSlot.trainer?._id,
        isRecurring,
        day: selectedSlot.day,
        studentId: selectedStudentId,
        userId: selectedParentId,
      },
      onSuccess: ({ status }) => {
        if (status === 200) {
          ShowToast("Class booked successfully", Severty.SUCCESS);
          fetchClassSlots();
          fetchClassData();
          setBookingLoading(false);
          handleCloseModal();
        }
      },
      onError: (err) => {
        const backendMessage =
          err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "Booking failed";

        ShowToast(backendMessage, Severty.ERROR);
        setBookingLoading(false);
      },
      onFinally: () => setBookingLoading(false),
    });
  };

  const handleParentSelect = (parentId) => {
    setSelectedParentId(parentId);
    setSelectedStudentId(null);
    setStudents([]);

    const params = new URLSearchParams(window.location.search);
    params.set("parentId", parentId);
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${params.toString()}`
    );

    fetchStudentsByParent(parentId);
  };

  useEffect(() => {
    fetchClassData();
    fetchClassSlots();
  }, [id]);

  const fetchClassData = async () => {
    setLoading(true);
    try {
      const response = await getClass(id);
      if (response.status === 200 && response.data) {
        setClassData(response.data);
      }
    } catch (error) {
      ShowToast(error.message || "Failed to fetch class data", Severty.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassSlots = async () => {
    setLoading(true);
    try {
      const response = await getClassSlots({ classId: id });
      if (response.status === 200 && response.data) {
        setSlots(response?.data?.data || []);
      }
    } catch (error) {
      ShowToast(error.message || "Failed to fetch class data", Severty.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useApi({
    callback: getEmployees,
    payload: {
      type: "trainer",
      branchId: classData?.branchId?._id || classData?.branchId,
    },
    dependencies: [],
    shouldCallApi: true,
    onSuccess: ({ data }) => {
      setTrainers(data);
    },
  });

  const handleView = (record) => {
    let slotId = record?._id;
    navigate(`/slot-students/${id}/${slotId}`);
  };

  const columns = [
    {
      title: "Time Slot",
      dataIndex: "slot",
      key: "slot",
      render: (text, record) => record?.startTime + "-" + record?.endTime,
    },
    {
      title: "Day",
      dataIndex: "day",
      key: "day",
      render: (text, record) => record?.day || "-",
    },
    {
      title: "Trainer",
      dataIndex: "trainer",
      key: "trainer",
      render: (text, record) => record?.trainer?.name || "-",
    },
    {
      title: "Capacity",
      dataIndex: "capacity",
      key: "capacity",
      render: (text, record) => record?.capacity || "-",
    },
    {
      title: "Total Booked Class",
      dataIndex: "currentWeekBookingCount",
      key: "currentWeekBookingCount",
      render: (text, record) => record?.currentWeekBookingCount || "0",
    },
    {
      title: "Recurring Spot Available",
      dataIndex: "nextWeekRemainingCapacity",
      key: "nextWeekRemainingCapacity",
      render: (text, record) => record?.nextWeekRemainingCapacity || "0",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record) => (
        <Tag>{record?.isActive ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const isFull = record?.remainingCapacity === 0;

        return (
          <Space size="middle">
            <Button
              onClick={() => handleView(record)}
              icon={<EyeOutlined />}
              title="View"
            />
            <Tooltip color="purple"
              title={isFull ? "This slot is already full!" : ""}
            >
              <Button
                title="Book Classes"
                disabled={isFull}
                onClick={() => handleReservationClick(record)}
              >
                Reservations
              </Button>
            </Tooltip>
          </Space>
        );
      },
    }
  ];

  return (
    <div>
      <Button
        type="default"
        className="mb-2"
        style={{ color: "black" }}
        onClick={() => window.history.back()}
      >
        Back
      </Button>

      <Card title="Class Schedules" className="mb-4 mt-4">
        <div className="table-responsive customPagination">
          <Table
            loading={loading}
            columns={columns}
            dataSource={slots || []}
            pagination={false}
            rowKey="_id"
            bordered
            size="middle"
          />
        </div>
      </Card>

      <Modal
        title="Book Classes"
        open={isReservationModalOpen}
        onCancel={handleCloseModal}
        onOk={handleBookClass}
        destroyOnClose
        okText="Book"
        okButtonProps={{ disabled: !selectedStudentId, loading: bookingLoading }}
      >


        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <strong>Slot:</strong>{" "}
            {selectedSlot?.startTime} - {selectedSlot?.endTime}
          </div>

          <div>
            <label>Parent</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Search & select parent"
              showSearch
              filterOption={false}
              value={selectedParentId}
              onSearch={handleParentSearch}
              onPopupScroll={handleParentPopupScroll}
              onChange={handleParentSelect}
              notFoundContent={
                parentLoading ? <Spin size="small" /> : "No parents found"
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {parentLoading && parents.length > 0 && (
                    <div style={{ textAlign: "center", padding: "8px 0" }}>
                      <Spin size="small" />
                    </div>
                  )}
                  {!parentLoading && parents.length > 0 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "4px 0",
                        color: "#999",
                        fontSize: 12,
                      }}
                    >
                      {parents.length} / {parentTotal} loaded
                    </div>
                  )}
                </>
              )}
              options={parents.map((parent) => ({
                value: parent._id,
                label: parent.name,
              }))}
            />
          </div>

          <div>
            <label>Student</label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select Student"
              disabled={!selectedParentId}
              value={selectedStudentId}
              onChange={(value) => {
                const student = students.find((s) => s._id === value);
                setSelectedStudentId(value);
                setSelectedStudent(student);
              }}
              options={students.map((student) => ({
                value: student._id,
                label: student.name,
              }))}
            />
          </div>

          <Checkbox
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
          >
            Recurring Class
          </Checkbox>
        </div>

      </Modal>

    </div>
  );
};

export default ClassViewPage;
